import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Form,
  Row,
  Col,
  Card,
  Dropdown,
  FormControl,
  OverlayTrigger,
  Popover,
  Collapse,
} from 'react-bootstrap';
import { QuestionCircle, ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import { canonicalMedicationKey, formatMedicationKey } from '../utils/medicationEquivalence';

export const MEDICATIONS = [
  'APIXABAN',
  'ASPIRIN',
  'CLOPIDOGREL',
  'DABIGATRAN_ETEXILATE',
  'EDOXABAN',
  'PRASUGREL',
  'RIVAROXABAN',
  'TICAGRELOR',
  'WARFARIN'
];

const EVENTS = [
  'CANCER_TREATMENT',
  'CEREBRAL_VASCULAR_ACCIDENT',
  'DEEP_VEIN_THROMBOSIS',
  'INTRACEREBRAL_HEMORRAGE',
  'LEFT_VENTRICULAR_THROMBUS',
  'PULMINARY_EMBOLISM',
  'STROKE',
  'THROMBOSIS',
  'TRANSIENT_ISCHAEMIC_ATTACK',
  'VENOUS_THROMBOEMBOLISM'
];

const CONDITIONS = [
  'ACTIVE_CANCER',
  'ALCOHOL_USE',
  'ANTI_PHOSPHOLIPID_SYNDROME',
  'ANTI_THROMBIN',
  'ATRIAL_FIBRILLATION',
  'CAGED_BALL_AORTIC_VALVE_PROSTHESIS',
  'CONGESTIVE_HEART_FAILURE',
  'DIABETES',
  'FACTOR_II_MUTATION',
  'HETEROZYGOUS_FACTOR_V',
  'HYPERTENSION',
  'MITRAL_MECHANICAL_PROSTHESIS',
  'MULTIPLE_ABNORMALITIES',
  'NON_SEVERE_THROMBOPHILIA',
  'PALLEATIVE_CARE_FOR_CANCER',
  'PRIOR_BLEEDING',
  'PRIOR_SIMILAR_PROCEDURE',
  'PROTEIN_C_DEFICIENCY',
  'PROTEIN_S_DEFICIENCY',
  'RECURRENT_VENOUS_THROMBOEMBOLISM',
  'RHEUMATIC_VALVE_HEART_DISEASE',
  'SUPRATHERAPEUTIC_INR',
  'THROMBOPHILIA',
  'TILTING_DISC_AORTIC_VALVE_PROSTHESIS',
  'VASCULAR_DISEASE'
];

const RISKS = [
  'ATRIAL_FIBRILLATION',
  'MODIFIABLE_PATIENT_BLEEDING',
  'PATIENT_BLEEDING',
  'PROCEDURAL_BLEEDING',
  'PROSTHETIC_HEART_VALVE',
  'SPINAL_PROCEDURAL',
  'THROMBOEMBOLIC',
  'THROMBOEMBOLIC_ANTICOAGULANT',
  'THROMBOEMBOLIC_ANTIPLATELET',
  'THROMBOSIS'
];

export const SURGERIES = [
  'ACDF_1_3',
  'ACDF_4_PLUS',
  'CERVICAL_CORPECTOMY',
  'CERVICAL_FUSION',
  'CERVICAL_LAMINECTOMY',
  'COFLEX',
  'DISCECTOMY_FORAMINOTOMY',
  'DIRECT_DECOMPRESSION',
  'INTRADURAL_SURGERY',
  'LAMINECTOMY',
  'LAMINOSPLASTY',
  'LUMBAR_FUSION',
  'LUMBAR_LAMINECTOMY',
  'LUMBAR_LAMIS_1_3',
  'LUMBAR_LAMIS_4_PLUS',
  'MINIMAL_ACCESS_TLIF',
  'NEURAXIAL_ANESTHESIA',
  'OPEN_TLIF',
  'SPINAL_CORD_STIMULATOR',
  'THORACIC_FUSION',
  'THORACIC_LAMINECTOMY'
];

const MedicationCheckboxForm = forwardRef(
  ({ periopResult, onSave, resetTrigger, notesHistoryPanel = null }, ref) => {
  const [added, setAdded] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [selectedMeds, setSelectedMeds] = useState(new Set());
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [eventDates, setEventDates] = useState({});
  const [selectedConditions, setSelectedConditions] = useState(new Set());
  const [selectedRisks, setSelectedRisks] = useState(new Set());
  const [dropdownSelections, setDropdownSelections] = useState({
    medication: new Set(),
    event: new Set(),
    condition: new Set(),
    risk: new Set()
  });
  const [selectedSurgery, setSelectedSurgery] = useState('');
  const [surgeryDate, setSurgeryDate] = useState('');
  const [showSurgerySection, setShowSurgerySection] = useState(true);
  const [customSurgery, setCustomSurgery] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [stagingSectionExpanded, setStagingSectionExpanded] = useState(false);
  /** Free-text note collapsible section — starts collapsed. */
  const [additionalInfoExpanded, setAdditionalInfoExpanded] = useState(false);
  const [notesHistoryExpanded, setNotesHistoryExpanded] = useState(false);

  // Add state to track if there are pending changes
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // Add surgery to the state management
  const [selectedSurgeries, setSelectedSurgeries] = useState(new Set());

  // Add useEffect to handle reset
  useEffect(() => {
    if (resetTrigger) {
      setAdded([]);
      setRemoved([]);
      
      // Re-initialize from periopResult
      if (periopResult) {
        const currentMeds = new Set();
        const currentEvents = new Set();
        const currentConditions = new Set();
        const currentRisks = new Set();
        const currentSurgeries = new Set(); // Add this
        const currentEventDates = {};

        if (periopResult.inputs) {
          const inputs = typeof periopResult.inputs === 'string' 
            ? JSON.parse(periopResult.inputs) 
            : periopResult.inputs;

          if (Array.isArray(inputs)) {
            inputs.forEach(item => {
              if (item.valid) {
                const formatKey = (key) => key.toUpperCase().replace(/\s+/g, '_');
                
                if (item.medication) {
                  const medKey = canonicalMedicationKey(formatMedicationKey(item.medication));
                  if (MEDICATIONS.includes(medKey)) {
                    currentMeds.add(medKey);
                  }
                }
                if (item.event) {
                  const eventKey = formatKey(item.event);
                  if (EVENTS.includes(eventKey)) {
                    currentEvents.add(eventKey);
                    if (item.date) {
                      currentEventDates[eventKey] = item.date;
                    }
                  }
                }
                if (item.condition) {
                  const conditionKey = formatKey(item.condition);
                  if (CONDITIONS.includes(conditionKey)) {
                    currentConditions.add(conditionKey);
                  }
                }
                if (item.risk) {
                  const riskKey = formatKey(item.risk);
                  if (RISKS.includes(riskKey)) {
                    currentRisks.add(riskKey);
                  }
                }
                if (item.scheduled_surgery) {
                  const surgeryKey = formatKey(item.scheduled_surgery.procedure);
                  if (SURGERIES.includes(surgeryKey)) {
                    currentSurgeries.add(surgeryKey);
                  }
                }
              }
            });
          }
        }

        if (periopResult.parsed_output) {
          const formatKey = (key) => {
            if (typeof key !== 'string' || !key) {
              return '';
            }
            return key.toUpperCase().replace(/\s+/g, '_');
          };

          Object.keys(periopResult.parsed_output.medications || {}).forEach(med => {
            const medKey = canonicalMedicationKey(formatMedicationKey(med));
            if (MEDICATIONS.includes(medKey)) {
              currentMeds.add(medKey);
            }
          });

          (periopResult.parsed_output.events || []).forEach(event => {
            const eventKey = formatKey(event);
            if (EVENTS.includes(eventKey)) {
              currentEvents.add(eventKey);
            }
          });

          (periopResult.parsed_output.conditions || []).forEach(condition => {
            const conditionKey = formatKey(condition);
            if (CONDITIONS.includes(conditionKey)) {
              currentConditions.add(conditionKey);
            }
          });

          Object.keys(periopResult.parsed_output.risks || {}).forEach(risk => {
            const riskKey = formatKey(risk);
            if (RISKS.includes(riskKey)) {
              currentRisks.add(riskKey);
            }
          });

          const surgeryData = periopResult.parsed_output.scheduled_surgery || periopResult.parsed_output.surgery;
          if (surgeryData) {
            const surgeryKey = formatKey(surgeryData.procedure);
            if (SURGERIES.includes(surgeryKey)) {
              currentSurgeries.add(surgeryKey);
            }
            setSurgeryDate(surgeryData.date || '');
          }
        }

        setSelectedMeds(currentMeds);
        setSelectedEvents(currentEvents);
        setSelectedConditions(currentConditions);
        setSelectedRisks(currentRisks);
        setSelectedSurgeries(currentSurgeries); // Add this
        setEventDates(currentEventDates);
      }
    }
  }, [resetTrigger, periopResult]);

  // Initialize checkboxes based on periopResult
  useEffect(() => {
    if (periopResult) {
      const currentMeds = new Set();
      const currentEvents = new Set();
      const currentConditions = new Set();
      const currentRisks = new Set();
      const currentSurgeries = new Set(); // Add this
      const currentEventDates = {};

      // Try to parse from inputs first - FILTERED VERSION
      if (periopResult.inputs) {
        const inputs = typeof periopResult.inputs === 'string' 
          ? JSON.parse(periopResult.inputs) 
          : periopResult.inputs;

        if (Array.isArray(inputs)) {
          inputs.forEach(item => {
            if (item.valid) {
              // Convert to uppercase and replace spaces with underscores
              const formatKey = (key) => {
                if (typeof key !== 'string' || !key) {
                  return '';
                }
                return key.toUpperCase().replace(/\s+/g, '_');
              };
              
              // Only process items that are in our defined lists
              if (item.medication) {
                const medKey = canonicalMedicationKey(formatMedicationKey(item.medication));
                if (MEDICATIONS.includes(medKey)) {
                  currentMeds.add(medKey);
                }
              }
              if (item.event) {
                const eventKey = formatKey(item.event);
                if (EVENTS.includes(eventKey)) {
                  currentEvents.add(eventKey);
                  if (item.date) {
                    currentEventDates[eventKey] = item.date;
                  }
                }
              }
              if (item.condition) {
                const conditionKey = formatKey(item.condition);
                if (CONDITIONS.includes(conditionKey)) {
                  currentConditions.add(conditionKey);
                }
              }
              if (item.risk) {
                const riskKey = formatKey(item.risk);
                if (RISKS.includes(riskKey)) {
                  currentRisks.add(riskKey);
                }
              }
              if (item.scheduled_surgery) {
                const surgeryKey = formatKey(item.scheduled_surgery.procedure);
                if (SURGERIES.includes(surgeryKey)) {
                  currentSurgeries.add(surgeryKey);
                }
              }
            }
          });
        }
      }

      // Then try to parse from parsed_output - also filtered
      if (periopResult.parsed_output) {
        const formatKey = (key) => {
          if (typeof key !== 'string' || !key) {
            return '';
          }
          return key.toUpperCase().replace(/\s+/g, '_');
        };
        
        // Add medications - only if in our list
        Object.keys(periopResult.parsed_output.medications || {}).forEach(med => {
          const medKey = canonicalMedicationKey(formatMedicationKey(med));
          if (MEDICATIONS.includes(medKey)) {
            currentMeds.add(medKey);
          }
        });

        // Add events - only if in our list
        (periopResult.parsed_output.events || []).forEach(event => {
          const eventKey = formatKey(event);
          if (EVENTS.includes(eventKey)) {
            currentEvents.add(eventKey);
          }
        });

        // Add conditions - only if in our list
        (periopResult.parsed_output.conditions || []).forEach(condition => {
          const conditionKey = formatKey(condition);
          if (CONDITIONS.includes(conditionKey)) {
            currentConditions.add(conditionKey);
          }
        });

        // Add risks - only if in our list
        Object.keys(periopResult.parsed_output.risks || {}).forEach(risk => {
          const riskKey = formatKey(risk);
          if (RISKS.includes(riskKey)) {
            currentRisks.add(riskKey);
          }
        });

        // Add surgeries - only if in our list
        (periopResult.parsed_output.scheduled_surgery || []).forEach(surgery => {
          // Handle both array and string procedures
          const procedures = Array.isArray(surgery.procedure) ? surgery.procedure : [surgery.procedure];
          procedures.forEach(procedure => {
            const surgeryKey = formatKey(procedure);
            if (SURGERIES.includes(surgeryKey)) {
              currentSurgeries.add(surgeryKey);
            }
          });
        });

        // Handle surgery - only if in our list
        const surgeryData = periopResult.parsed_output.scheduled_surgery || periopResult.parsed_output.surgery;
        if (surgeryData) {
          // Handle both array and string procedures
          const procedures = Array.isArray(surgeryData.procedure) ? surgeryData.procedure : [surgeryData.procedure];
          procedures.forEach(procedure => {
            const surgeryKey = formatKey(procedure);
            if (SURGERIES.includes(surgeryKey)) {
              currentSurgeries.add(surgeryKey);
            }
          });
          setSurgeryDate(surgeryData.date || '');
        }
      }

      setSelectedMeds(currentMeds);
      setSelectedEvents(currentEvents);
      setSelectedConditions(currentConditions);
      setSelectedRisks(currentRisks);
      setSelectedSurgeries(currentSurgeries); // Add this
      setEventDates(currentEventDates);
    }
  }, [periopResult]);

  // Add useEffect to log state changes
  useEffect(() => {
    console.log('Current State:', {
      added,
      removed
    });
  }, [added, removed]);

  // Add useEffect to track pending changes
  useEffect(() => {
    const hasChanges = added.length > 0 || removed.length > 0 || additionalInfo.trim() !== '';
    setHasPendingChanges(hasChanges);
  }, [added, removed, additionalInfo]);

  useEffect(() => {
    if (added.length > 0 || removed.length > 0) {
      setStagingSectionExpanded(true);
    }
  }, [added, removed]);

  

  const handleDropdownSelect = (type, value, selectedSet, setSelected) => {
    // Only proceed if the item isn't already selected
    if (!selectedSet.has(value)) {
      // Update the checkbox display
      const newSelected = new Set(selectedSet);
      newSelected.add(value);
      setSelected(newSelected);
      
      // Update dropdown selections tracking
      const newDropdownSelections = {
        ...dropdownSelections,
        [type]: new Set(dropdownSelections[type]).add(value)
      };
      setDropdownSelections(newDropdownSelections);
      
      // Special handling for surgeries - group them into a single entry
      if (type === 'scheduled_surgery') {
        // Remove all existing surgery entries
        const filteredAdded = added.filter(item => !item.scheduled_surgery);
        
        // Add all selected surgeries as a single entry with procedures array
        if (newSelected.size > 0 && surgeryDate) {
          const surgeryItem = {
            scheduled_surgery: {
              date: surgeryDate,
              procedure: Array.from(newSelected) // Send as array of procedures
            },
            valid: true
          };
          
          setAdded([...filteredAdded, surgeryItem]);
          console.log('After dropdown select surgery - Added:', [...filteredAdded, surgeryItem]);
        }
      } else {
        // Check if item is already in added array
        const alreadyAdded = added.some(item => item[type] === value && item.valid === true);
        if (!alreadyAdded) {
          // Add to added array
          const newItem = { [type]: value, valid: true };
          setAdded(prev => {
            const newAdded = [...prev, newItem];
            console.log('After dropdown select - Added:', newAdded);
            return newAdded;
          });
        }
      }
    }
  };

  const handleCheckboxChange = (type, value, selectedSet, setSelected) => {
    const newSelected = new Set(selectedSet);
    const itemToCheck = { [type]: value, valid: true };

    if (newSelected.has(value)) {
      // Item is being deselected
      newSelected.delete(value);
      
      // Check if item is in added array
      const addedIndex = added.findIndex(item => 
        item[type] === value && item.valid === true
      );

      if (addedIndex !== -1) {
        // Remove from added array
        setAdded(prev => {
          const newAdded = prev.filter((_, index) => index !== addedIndex);
          console.log('After removing from added:', newAdded);
          return newAdded;
        });
      } else {
        // Check if item is already in removed array
        const alreadyRemoved = removed.some(item => item[type] === value && item.valid === true);
        if (!alreadyRemoved) {
          // Add to removed array if not in added and not already in removed
          setRemoved(prev => {
            const newRemoved = [...prev, itemToCheck];
            console.log('After adding to removed:', newRemoved);
            return newRemoved;
          });
        }
      }

      if (type === 'event') {
        const newEventDates = { ...eventDates };
        delete newEventDates[value];
        setEventDates(newEventDates);
      }
    } else {
      // Item is being selected
      newSelected.add(value);
      
      // Check if item is in removed array
      const removedIndex = removed.findIndex(item => 
        item[type] === value && item.valid === true
      );

      if (removedIndex !== -1) {
        // Remove from removed array
        setRemoved(prev => {
          const newRemoved = prev.filter((_, index) => index !== removedIndex);
          console.log('After removing from removed:', newRemoved);
          return newRemoved;
        });
      } else {
        // Check if item is already in added array
        const alreadyAdded = added.some(item => item[type] === value && item.valid === true);
        if (!alreadyAdded) {
          // Add to added array if not in removed and not already in added
          setAdded(prev => {
            const newAdded = [...prev, itemToCheck];
            console.log('After adding to added:', newAdded);
            return newAdded;
          });
        }
      }

      if (type === 'event') {
        const newEventDates = { ...eventDates };
        newEventDates[value] = new Date().toISOString().split('T')[0];
        setEventDates(newEventDates);
      }
    }
    setSelected(newSelected);

    // Special handling for surgeries - update all surgeries as a group
    if (type === 'scheduled_surgery') {
      // Remove all existing surgery entries
      const filteredAdded = added.filter(item => !item.scheduled_surgery);
      
      // Add all selected surgeries as a single entry with procedures array
      if (newSelected.size > 0 && surgeryDate) {
        const surgeryItem = {
          scheduled_surgery: {
            date: surgeryDate,
            procedure: Array.from(newSelected) // Send as array of procedures
          },
          valid: true
        };
        
        setAdded([...filteredAdded, surgeryItem]);
        console.log('After updating surgeries - Added:', [...filteredAdded, surgeryItem]);
      }
    }
  };

  const handleEventDateChange = (event, value) => {
    const newEventDates = { ...eventDates };
    newEventDates[value] = event.target.value;
    setEventDates(newEventDates);
    
    // Update the event in added array
    const addedIndex = added.findIndex(item => 
      item.event === value && item.valid === true
    );

    if (addedIndex !== -1) {
      const updatedAdded = [...added];
      updatedAdded[addedIndex] = {
        ...updatedAdded[addedIndex],
        date: newEventDates[value]
      };
      setAdded(updatedAdded);
      console.log('After updating event date - Added:', updatedAdded);
    }
  };

  const handleSurgeryUpdate = (newSurgery = null, newDate = null) => {
    const surgeryToUse = newSurgery || (selectedSurgery === 'OTHER' ? customSurgery : selectedSurgery);
    const dateToUse = newDate || surgeryDate;
    
    if (surgeryToUse && dateToUse) {
      const surgeryItem = {
        scheduled_surgery: {
          date: dateToUse,
          procedure: surgeryToUse.replace(/_/g, ' ')
        },
        valid: true
      };

      // Remove any existing surgery entries from added array
      const filteredAdded = added.filter(item => !item.scheduled_surgery);
      
      // Add the new surgery item
      const updatedAdded = [...filteredAdded, surgeryItem];
        setAdded(updatedAdded);
        console.log('After updating surgery - Added:', updatedAdded);
    }
  };

  const generateNoteText = () => {
    const sections = [];
    
    if (selectedMeds.size > 0) {
      sections.push(`Medications: ${Array.from(selectedMeds).join(', ')}`);
    }
    if (selectedEvents.size > 0) {
      sections.push(`Events: ${Array.from(selectedEvents).join(', ')}`);
    }
    if (selectedConditions.size > 0) {
      sections.push(`Conditions: ${Array.from(selectedConditions).join(', ')}`);
    }
    if (selectedRisks.size > 0) {
      sections.push(`Risks: ${Array.from(selectedRisks).join(', ')}`);
    }

    return sections.join('\n');
  };

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      ref={ref}
      onClick={onClick}
      className="form-control text-start"
      style={{ cursor: 'pointer' }}
    >
      {children}
    </button>
  ));

  const CustomMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      const [value, setValue] = useState('');

      return (
        <div
          ref={ref}
          style={{ ...style, maxHeight: '200px', overflowY: 'auto' }}
          className={className}
          aria-labelledby={labeledBy}
        >
          <FormControl
            autoFocus
            className="mx-3 my-2 w-auto"
            placeholder="Type to filter..."
            onChange={(e) => setValue(e.target.value)}
            value={value}
          />
          <ul className="list-unstyled mb-0">
            {React.Children.toArray(children).filter(
              (child) =>
                !value || child.props.children.toLowerCase().includes(value.toLowerCase())
            )}
          </ul>
        </div>
      );
    }
  );

  const CheckboxSection = ({ title, items, selected, setSelected, type }) => (
    <Card className="h-100">
      <Card.Body>
        <Card.Title>{title}</Card.Title>
        
        {/* Searchable Dropdown */}
        <Dropdown className="mb-3">
          <Dropdown.Toggle as={CustomToggle} id={`dropdown-${type}`}>
            Select {title}...
          </Dropdown.Toggle>

          <Dropdown.Menu as={CustomMenu}>
            {items.map((item) => (
              <Dropdown.Item
                key={item}
                onClick={() => handleDropdownSelect(type, item, selected, setSelected)}
                active={selected.has(item)}
              >
                {item.replace(/_/g, ' ')}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {/* Selected Items - Made Scrollable */}
        <div className="selected-items" style={{ 
          maxHeight: '200px', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          border: '1px solid #dee2e6', 
          borderRadius: '0.375rem', 
          padding: '0.5rem' 
        }}>
          {Array.from(selected).sort().map((item) => (
            <div key={item} className="mb-2">
              <div className="d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id={`${type}-${item}`}
                  label={item.replace(/_/g, ' ')}
                  checked={true}
                  onChange={() => handleCheckboxChange(type, item, selected, setSelected)}
                />
              </div>
              {type === 'event' && (
                <div className="ms-4 mt-1">
                  <Form.Control
                    type="date"
                    value={eventDates[item] || ''}
                    onChange={(e) => handleEventDateChange(e, item)}
                    style={{ width: '150px' }}
                  />
                </div>
              )}
            </div>
          ))}
          {selected.size === 0 && (
            <div className="text-muted text-center py-2">No {title.toLowerCase()} selected</div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  const ItemPill = ({ item, onRemove, type }) => {
    const getDisplayText = () => {
      if (item.scheduled_surgery) {
        return `Surgery: ${item.scheduled_surgery.procedure} (${item.scheduled_surgery.date})`;
      }
      if (item.medication) return `Medication: ${item.medication}`;
      if (item.event) return `Event: ${item.event}${item.date ? ` (${item.date})` : ''}`;
      if (item.condition) return `Condition: ${item.condition}`;
      if (item.risk) return `Risk: ${item.risk}`;
      return '';
    };

    // Light pills: BS 5.3 subtle backgrounds + emphasis text (stable vs bg-opacity on colored bg)
    const pillClass =
      type === 'added'
        ? 'bg-success-subtle text-success-emphasis border border-success border-opacity-25'
        : 'bg-warning-subtle text-warning-emphasis border border-warning border-opacity-25';

    // Show X on hover
    const [hovered, setHovered] = React.useState(false);

    return (
      <span
        className={`d-inline-flex align-items-center rounded-pill px-3 py-1 me-2 mb-2 ${pillClass}`}
        style={{ position: 'relative', fontSize: '1rem', cursor: 'default' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <button
          style={{
            background: 'none',
            border: 'none',
            color: '#888',
            fontSize: '1.1em',
            marginRight: '4px',
            marginLeft: '-4px',
            cursor: 'pointer',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.2s',
            padding: 0,
            lineHeight: 1,
            position: 'relative',
            top: '1px',
          }}
          onClick={() => onRemove(item)}
          aria-label="Remove"
          tabIndex={0}
        >
          ×
        </button>
        {getDisplayText()}
      </span>
    );
  };

  const handleRemoveItem = (item, arrayType) => {
    // Remove from added/removed arrays as before
    if (arrayType === 'added') {
      const newAdded = added.filter(i => {
        if (item.scheduled_surgery) {
          return !i.scheduled_surgery || i.scheduled_surgery.procedure !== item.scheduled_surgery.procedure;
        }
        return !Object.entries(item).every(([key, value]) => i[key] === value);
      });
      setAdded(newAdded);
      // Uncheck the box (already implemented below)
      if (item.medication) {
        setSelectedMeds(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.medication);
          return newSet;
        });
      }
      if (item.event) {
        setSelectedEvents(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.event);
          return newSet;
        });
        setEventDates(prev => {
          const newDates = { ...prev };
          delete newDates[item.event];
          return newDates;
        });
      }
      if (item.condition) {
        setSelectedConditions(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.condition);
          return newSet;
        });
      }
      if (item.risk) {
        setSelectedRisks(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.risk);
          return newSet;
        });
      }
      if (item.scheduled_surgery) {
        setSelectedSurgeries(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.scheduled_surgery.procedure);
          return newSet;
        });
        setSelectedSurgery('');
        setSurgeryDate('');
        setCustomSurgery('');
        setShowCustomInput(false);
      }
    } else {
      const newRemoved = removed.filter(i => {
        if (item.scheduled_surgery) {
          return !i.scheduled_surgery || i.scheduled_surgery.procedure !== item.scheduled_surgery.procedure;
        }
        return !Object.entries(item).every(([key, value]) => i[key] === value);
      });
      setRemoved(newRemoved);
      // Re-check the box (add back to selection set)
      if (item.medication) {
        setSelectedMeds(prev => {
          const newSet = new Set(prev);
          newSet.add(item.medication);
          return newSet;
        });
      }
      if (item.event) {
        setSelectedEvents(prev => {
          const newSet = new Set(prev);
          newSet.add(item.event);
          return newSet;
        });
        if (item.date) {
          setEventDates(prev => ({ ...prev, [item.event]: item.date }));
        }
      }
      if (item.condition) {
        setSelectedConditions(prev => {
          const newSet = new Set(prev);
          newSet.add(item.condition);
          return newSet;
        });
      }
      if (item.risk) {
        setSelectedRisks(prev => {
          const newSet = new Set(prev);
          newSet.add(item.risk);
          return newSet;
        });
      }
      if (item.scheduled_surgery) {
        setSelectedSurgeries(prev => {
          const newSet = new Set(prev);
          newSet.add(item.scheduled_surgery.procedure);
          return newSet;
        });
        setSelectedSurgery(item.scheduled_surgery.procedure || '');
        setSurgeryDate(item.scheduled_surgery.date || '');
      }
    }
  };

  // Add a save handler
  const handleSave = () => {
    if (onSave) {
      onSave({ added, removed, additionalInfo });
    }
  };

  useImperativeHandle(ref, () => ({
    handleSave,
    hasPendingChanges
  }));

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <Form.Label className="mb-0 me-2">Update Patient Information</Form.Label>
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="right"
            overlay={
              <Popover id="update-patient-info-help">
                <Popover.Body>
                  Use this form to add, remove, or modify patient information that will be used to generate perioperative medication recommendations. In order to generate a new recommendation, you must save your changes. Please note that any information added here will not alter any data in your EHR system.
                </Popover.Body>
              </Popover>
            }
          >
            <div className="d-inline-flex align-items-center" style={{ cursor: 'help' }}>
              <QuestionCircle className="text-muted" size={16} />
            </div>
          </OverlayTrigger>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={() => {
            setAdded([]);
            setRemoved([]);
            setAdditionalInfo('');
            
            // Re-initialize from periopResult
            if (periopResult) {
              const currentMeds = new Set();
              const currentEvents = new Set();
              const currentConditions = new Set();
              const currentRisks = new Set();
              const currentSurgeries = new Set(); // Add this
              const currentEventDates = {};

              if (periopResult.inputs) {
                const inputs = typeof periopResult.inputs === 'string' 
                  ? JSON.parse(periopResult.inputs) 
                  : periopResult.inputs;

                if (Array.isArray(inputs)) {
                  inputs.forEach(item => {
                    if (item.valid) {
                      const formatKey = (key) => key.toUpperCase().replace(/\s+/g, '_');
                      
                      if (item.medication) {
                        const medKey = canonicalMedicationKey(formatMedicationKey(item.medication));
                        if (MEDICATIONS.includes(medKey)) {
                          currentMeds.add(medKey);
                        }
                      }
                      if (item.event) {
                        const eventKey = formatKey(item.event);
                        if (EVENTS.includes(eventKey)) {
                          currentEvents.add(eventKey);
                          if (item.date) {
                            currentEventDates[eventKey] = item.date;
                          }
                        }
                      }
                      if (item.condition) {
                        const conditionKey = formatKey(item.condition);
                        if (CONDITIONS.includes(conditionKey)) {
                          currentConditions.add(conditionKey);
                        }
                      }
                      if (item.risk) {
                        const riskKey = formatKey(item.risk);
                        if (RISKS.includes(riskKey)) {
                          currentRisks.add(riskKey);
                        }
                      }
                      if (item.scheduled_surgery) {
                        const surgeryKey = formatKey(item.scheduled_surgery.procedure);
                        if (SURGERIES.includes(surgeryKey)) {
                          currentSurgeries.add(surgeryKey);
                        }
                      }
                    }
                  });
                }
              }

              if (periopResult.parsed_output) {
                const formatKey = (key) => {
                  if (typeof key !== 'string' || !key) {
                    return '';
                  }
                  return key.toUpperCase().replace(/\s+/g, '_');
                };
                
                Object.keys(periopResult.parsed_output.medications || {}).forEach(med => {
                  const medKey = canonicalMedicationKey(formatMedicationKey(med));
                  if (MEDICATIONS.includes(medKey)) {
                    currentMeds.add(medKey);
                  }
                });

                (periopResult.parsed_output.events || []).forEach(event => {
                  const eventKey = formatKey(event);
                  if (EVENTS.includes(eventKey)) {
                    currentEvents.add(eventKey);
                  }
                });

                (periopResult.parsed_output.conditions || []).forEach(condition => {
                  const conditionKey = formatKey(condition);
                  if (CONDITIONS.includes(conditionKey)) {
                    currentConditions.add(conditionKey);
                  }
                });

                Object.keys(periopResult.parsed_output.risks || {}).forEach(risk => {
                  const riskKey = formatKey(risk);
                  if (RISKS.includes(riskKey)) {
                    currentRisks.add(riskKey);
                  }
                });

                // Add surgeries - only if in our list
                (periopResult.parsed_output.scheduled_surgery || []).forEach(surgery => {
                  // Handle both array and string procedures
                  const procedures = Array.isArray(surgery.procedure) ? surgery.procedure : [surgery.procedure];
                  procedures.forEach(procedure => {
                    const surgeryKey = formatKey(procedure);
                    if (SURGERIES.includes(surgeryKey)) {
                      currentSurgeries.add(surgeryKey);
                    }
                  });
                });

                const surgeryData = periopResult.parsed_output.scheduled_surgery || periopResult.parsed_output.surgery;
                if (surgeryData) {
                  // Handle both array and string procedures
                  const procedures = Array.isArray(surgeryData.procedure) ? surgeryData.procedure : [surgeryData.procedure];
                  procedures.forEach(procedure => {
                    const surgeryKey = formatKey(procedure);
                    if (SURGERIES.includes(surgeryKey)) {
                      currentSurgeries.add(surgeryKey);
                    }
                  });
                  setSurgeryDate(surgeryData.date || '');
                }
              }

              setSelectedMeds(currentMeds);
              setSelectedEvents(currentEvents);
              setSelectedConditions(currentConditions);
              setSelectedRisks(currentRisks);
              setSelectedSurgeries(currentSurgeries); // Add this
              setEventDates(currentEventDates);
            }
          }}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Reset Note
        </button>
      </div>
      <Row className="g-3">
        {/* Surgery Section */}
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Surgery Information</Card.Title>
              
              {/* Surgery Date */}
              <Form.Group className="mb-3">
                <Form.Label>Surgery Date</Form.Label>
                <Form.Control
                  type="date"
                  value={surgeryDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setSurgeryDate(newDate);
                    
                    // Update all selected surgeries with the new date
                    if (newDate && selectedSurgeries.size > 0) {
                      const filteredAdded = added.filter(item => !item.scheduled_surgery);
                      const surgeryItem = {
                        scheduled_surgery: {
                          date: newDate,
                          procedure: Array.from(selectedSurgeries) // Send as array of procedures
                        },
                        valid: true
                      };
                      setAdded([...filteredAdded, surgeryItem]);
                    }
                  }}
                />
              </Form.Group>

              {/* Surgery Selection - Inline CheckboxSection */}
              <div className="surgery-selection">
                <Form.Label>Procedures</Form.Label>
                
                {/* Searchable Dropdown */}
                <Dropdown className="mb-3">
                  <Dropdown.Toggle as={CustomToggle} id="dropdown-surgery">
                    Select Procedures...
                  </Dropdown.Toggle>

                  <Dropdown.Menu as={CustomMenu}>
                    {SURGERIES.map((surgery) => (
                      <Dropdown.Item
                        key={surgery}
                        onClick={() => handleDropdownSelect('scheduled_surgery', surgery, selectedSurgeries, setSelectedSurgeries)}
                        active={selectedSurgeries.has(surgery)}
                      >
                        {surgery.replace(/_/g, ' ')}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {/* Selected Items - Made Scrollable */}
                <div className="selected-items" style={{ 
                  maxHeight: '150px', 
                  overflowY: 'auto', 
                  overflowX: 'hidden',
                  border: '1px solid #dee2e6', 
                  borderRadius: '0.375rem', 
                  padding: '0.5rem' 
                }}>
                  {Array.from(selectedSurgeries).sort().map((surgery) => (
                    <div key={surgery} className="mb-2">
                      <div className="d-flex align-items-center">
                        <Form.Check
                          type="checkbox"
                          id={`surgery-${surgery}`}
                          label={surgery.replace(/_/g, ' ')}
                          checked={true}
                          onChange={() => handleCheckboxChange('scheduled_surgery', surgery, selectedSurgeries, setSelectedSurgeries)}
                        />
                      </div>
                    </div>
                  ))}
                  {selectedSurgeries.size === 0 && (
                    <div className="text-muted text-center py-2">No procedures selected</div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Medications Section */}
        <Col md={3}>
          <CheckboxSection
            title="Medications"
            items={MEDICATIONS}
            selected={selectedMeds}
            setSelected={setSelectedMeds}
            type="medication"
          />
        </Col>

        {/* Events Section */}
        <Col md={3}>
          <CheckboxSection
            title="Events"
            items={EVENTS}
            selected={selectedEvents}
            setSelected={setSelectedEvents}
            type="event"
          />
        </Col>

        {/* Conditions Section */}
        <Col md={3}>
          <CheckboxSection
            title="Conditions"
            items={CONDITIONS}
            selected={selectedConditions}
            setSelected={setSelectedConditions}
            type="condition"
          />
        </Col>
      </Row>
      <div className="mt-3 medication-staged-changes-section">
          <button
            type="button"
            className="btn btn-link text-start text-decoration-none p-0 mb-2 d-flex align-items-center gap-2 text-body"
            onClick={() => setStagingSectionExpanded((e) => !e)}
            aria-expanded={stagingSectionExpanded}
            aria-controls="medication-staged-changes-collapse"
            id="medication-staged-changes-toggle"
          >
            {stagingSectionExpanded ? (
              <ChevronDown size={14} className="flex-shrink-0 text-secondary" aria-hidden />
            ) : (
              <ChevronRight size={14} className="flex-shrink-0 text-secondary" aria-hidden />
            )}
            <span className="fw-semibold small">Added &amp; removed items</span>
          </button>
          <Collapse in={stagingSectionExpanded}>
            <div id="medication-staged-changes-collapse">
              <Row>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <Card.Title>Added Items</Card.Title>
                      <div className="d-flex flex-wrap">
                        {added.map((item, index) => (
                          <ItemPill
                            key={`added-${index}`}
                            item={item}
                            onRemove={(item) => handleRemoveItem(item, 'added')}
                            type="added"
                          />
                        ))}
                        {added.length === 0 && (
                          <div className="text-muted">No items added</div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <Card.Title>Removed Items</Card.Title>
                      <div className="d-flex flex-wrap">
                        {removed.map((item, index) => (
                          <ItemPill
                            key={`removed-${index}`}
                            item={item}
                            onRemove={(item) => handleRemoveItem(item, 'removed')}
                            type="removed"
                          />
                        ))}
                        {removed.length === 0 && (
                          <div className="text-muted">No items removed</div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Collapse>
        </div>
      <div className="mt-3 medication-additional-information-section">
        <button
          type="button"
          className="btn btn-link text-start text-decoration-none p-0 mb-2 d-flex align-items-center gap-2 text-body"
          onClick={() => setAdditionalInfoExpanded((e) => !e)}
          aria-expanded={additionalInfoExpanded}
          aria-controls="medication-additional-info-collapse"
          id="medication-additional-info-toggle"
        >
          {additionalInfoExpanded ? (
            <ChevronDown size={14} className="flex-shrink-0 text-secondary" aria-hidden />
          ) : (
            <ChevronRight size={14} className="flex-shrink-0 text-secondary" aria-hidden />
          )}
          <span className="fw-semibold small">Add text note to patient record</span>
        </button>
        <Collapse in={additionalInfoExpanded}>
          <div id="medication-additional-info-collapse">
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={3}
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Enter additional patient information..."
                aria-label="Text note for patient record"
              />
            </Form.Group>
          </div>
        </Collapse>
      </div>

      {notesHistoryPanel && (
        <div
          id="additional_notes_history"
          className="mt-3 medication-notes-history-panel"
          style={{ scrollMarginTop: '0.75rem' }}
        >
          <button
            type="button"
            className="btn btn-link text-start text-decoration-none p-0 mb-2 d-flex align-items-center gap-2 text-body"
            onClick={() => setNotesHistoryExpanded((e) => !e)}
            aria-expanded={notesHistoryExpanded}
            aria-controls="additional-notes-history-collapse"
            id="medication-notes-history-toggle"
          >
            {notesHistoryExpanded ? (
              <ChevronDown size={14} className="flex-shrink-0 text-secondary" aria-hidden />
            ) : (
              <ChevronRight size={14} className="flex-shrink-0 text-secondary" aria-hidden />
            )}
            <span className="fw-semibold small">Additional Notes History</span>
          </button>
          <Collapse in={notesHistoryExpanded}>
            <div id="additional-notes-history-collapse">{notesHistoryPanel}</div>
          </Collapse>
        </div>
      )}
    </div>
  );
});

export default MedicationCheckboxForm; 